'use client'

import * as React from 'react'
import { motion } from 'motion/react'

import { Avatar, AvatarFallback, AvatarImage } from '@ui/web/components'
import { TrophyIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { RewardStar } from './reward-star'

export function xpToStars(xp: number, maxXp: number): 0 | 1 | 2 | 3 {
  if (maxXp <= 0) return 0
  const pct = (xp / maxXp) * 100
  if (pct <= 25) return 0
  if (pct <= 50) return 1
  if (pct <= 75) return 2
  return 3
}

const GRADE_LABELS: Record<0 | 1 | 2 | 3, string> = {
  0: 'Berkembang',
  1: 'Cukup',
  2: 'Baik',
  3: 'Sangat Baik',
}

export function xpToGradeLabel(xp: number, maxXp: number): string {
  const stars = xpToStars(xp, maxXp)
  const pct = maxXp > 0 ? Math.min(100, Math.round((xp / maxXp) * 100)) : 0
  if (stars === 3 && pct === 100) return 'Sempurna!'
  return GRADE_LABELS[stars]
}

export interface LeaderboardCardProps {
  rank: number
  showTrophy?: boolean
  avatarSrc: string
  name: string
  grade: string
  schoolName: string
  xp: number
  maxXp: number
  className?: string
  progress: number
}

export function LeaderboardCard({
  rank,
  showTrophy,
  avatarSrc,
  name,
  grade,
  schoolName,
  xp,
  maxXp,
  className,
  progress,
}: LeaderboardCardProps) {
  const unstarted = rank === 0
  const stars = xpToStars(xp, maxXp)
  const pct = maxXp > 0 ? Math.min(100, Math.round((xp / maxXp) * 100)) : 0
  const gradeLabel = stars === 3 && pct === 100 ? 'Sempurna!' : GRADE_LABELS[stars]

  return (
    <div className={cn('srd:@container srd:rounded-2xl srd:bg-neutral-white srd:border srd:border-grey-20', className)}>
      {/* NARROW layout — default, hidden at ≥360px */}
      <div className='srd:flex srd:flex-col srd:gap-2 srd:p-4 srd:@[360px]:hidden'>
        {/* rank left, trophy right */}
        <div className='srd:flex srd:items-center srd:justify-between'>
          <div className='srd:flex srd:items-center srd:justify-center srd:rounded-md srd:bg-grey-20 srd:min-w-8 srd:h-8 srd:px-2'>
            <span
              className={cn(
                typographyVariants({ variant: 'body-md' }),
                'srd:text-neutral-black srd:leading-none srd:font-bold',
              )}
            >
              {unstarted ? '-' : rank}
            </span>
          </div>
          {showTrophy && (
            <div className='srd:flex srd:items-center srd:justify-center srd:rounded-md srd:bg-purple-20 srd:size-6'>
              <TrophyIcon size={16} color='var(--purple-100)' />
            </div>
          )}
        </div>

        {/* avatar+name left, stars+xp right */}
        <div className='srd:flex srd:items-center srd:justify-between srd:gap-3'>
          <div className='srd:flex srd:items-center srd:gap-2 srd:min-w-0 srd:flex-1'>
            <Avatar profile='user' className='srd:size-8 srd:shrink-0'>
              <AvatarImage src={avatarSrc ?? undefined} alt={name} />
              <AvatarFallback>{getInitials(name)}</AvatarFallback>
            </Avatar>
            <div className='srd:flex srd:flex-col srd:min-w-0'>
              <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black srd:truncate')}>
                {name}
              </span>
              <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black srd:truncate')}>
                {grade ? `${grade} • ${schoolName}` : schoolName}
              </span>
            </div>
          </div>
          <div className='srd:flex srd:flex-col srd:items-center srd:shrink-0'>
            <RewardStar stars={stars} className='srd:w-13' middleClassName='srd:-translate-y-1' />
            <span
              className={cn(
                typographyVariants({ variant: 'body-md' }),
                'srd:text-neutral-black srd:font-bold srd:tabular-nums',
              )}
            >
              {unstarted ? '-' : xp.toLocaleString('id-ID')}
            </span>
          </div>
        </div>

        {/* progress full width */}
        <div className='srd:flex srd:items-center srd:gap-2'>
          <div className='srd:flex srd:items-center srd:gap-2 srd:w-full srd:max-w-[70%]'>
            <div className='srd:flex-1 srd:h-2 srd:rounded-full srd:overflow-hidden srd:bg-grey-20'>
              <motion.div
                className='srd:h-full srd:rounded-full'
                style={{ background: 'var(--purple-60)' }}
                initial={{ width: '0%' }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
              />
            </div>
            <span
              className={cn(
                typographyVariants({ variant: 'body-sm' }),
                'srd:text-grey-60 srd:shrink-0 srd:tabular-nums',
              )}
            >
              {unstarted ? '-' : `${progress}%`}
            </span>
          </div>
          {!unstarted && gradeLabel && (
            <span
              className={cn(
                typographyVariants({ variant: 'body-md' }),
                gradeLabel === 'Sempurna!' ? 'srd:text-purple-100' : 'srd:text-grey-60',
                'srd:shrink-0',
              )}
            >
              {gradeLabel}
            </span>
          )}
        </div>
      </div>

      {/* WIDE layout — hidden below 360px */}
      <div className='srd:hidden srd:@[360px]:flex srd:gap-3 srd:p-4'>
        {/* left col: rank top, trophy bottom */}
        <div className='srd:flex srd:flex-col srd:items-center srd:justify-between srd:shrink-0'>
          <div className='srd:flex srd:items-center srd:justify-center srd:rounded-md srd:bg-grey-20 srd:min-w-8 srd:h-8 srd:px-2'>
            <span
              className={cn(
                typographyVariants({ variant: 'body-md' }),
                'srd:text-neutral-black srd:leading-none srd:font-bold',
              )}
            >
              {unstarted ? '-' : rank}
            </span>
          </div>
          {showTrophy && (
            <div className='srd:flex srd:items-center srd:justify-center srd:rounded-md srd:bg-purple-20 srd:size-6'>
              <TrophyIcon size={16} color='var(--purple-100)' />
            </div>
          )}
        </div>

        {/* center: avatar+name row, then progress row */}
        <div className='srd:flex srd:flex-col srd:flex-1 srd:min-w-0 srd:gap-2'>
          <div className='srd:flex srd:items-center srd:gap-2 srd:min-w-0'>
            <Avatar profile='user' className='srd:size-8 srd:shrink-0'>
              <AvatarImage src={avatarSrc ?? undefined} alt={name} />
              <AvatarFallback>{getInitials(name)}</AvatarFallback>
            </Avatar>
            <div className='srd:flex srd:flex-col srd:min-w-0'>
              <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black srd:truncate')}>
                {name}
              </span>
              <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black srd:truncate')}>
                {grade ? `${grade} • ${schoolName}` : schoolName}
              </span>
            </div>
          </div>
          <div className='srd:flex srd:items-center srd:gap-2 srd:w-full srd:max-w-[256px]'>
            <div className='srd:flex-1 srd:h-2 srd:rounded-full srd:overflow-hidden srd:bg-grey-20'>
              <motion.div
                className='srd:h-full srd:rounded-full'
                style={{ background: 'var(--purple-60)' }}
                initial={{ width: '0%' }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
              />
            </div>
            <span
              className={cn(
                typographyVariants({ variant: 'body-sm' }),
                'srd:text-grey-60 srd:shrink-0 srd:tabular-nums',
              )}
            >
              {unstarted ? '-' : `${progress}%`}
            </span>
          </div>
        </div>

        {/* right col: stars top, xp+label bottom */}
        <div className={cn('srd:flex srd:flex-col srd:items-center srd:shrink-0', gradeLabel && 'srd:justify-between')}>
          <RewardStar stars={stars} className='srd:w-13' middleClassName='srd:-translate-y-1' />
          <div className='srd:flex srd:flex-col srd:items-center'>
            <span
              className={cn(
                typographyVariants({ variant: 'body-md' }),
                'srd:text-neutral-black srd:font-bold srd:tabular-nums',
              )}
            >
              {unstarted ? '-' : xp.toLocaleString('id-ID')}
            </span>
            {!unstarted && gradeLabel && (
              <span
                className={cn(
                  typographyVariants({ variant: 'body-md' }),
                  gradeLabel === 'Sempurna!' ? 'srd:text-purple-100' : 'srd:text-grey-60',
                  'srd:shrink-0',
                )}
              >
                {gradeLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
